import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { MessageSquare, Send, X, RefreshCw, Download, Type } from 'lucide-react';
import { widgetTheme, getBackgroundDataURI } from '@/config/widgetTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reliable mobile detection using user agent (not window width which fails in iframes)
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  // Check for mobile keywords in user agent
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
};

export default function ChatWidget() {
  const { client_number } = useParams();
  
  const getStorageKey = (key) => `icebreak-${client_number}-${key}`;
  
  const safeLocalStorage = {
    getItem: (key) => {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn('localStorage access denied:', e);
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.warn('localStorage write failed:', e);
      }
    },
    removeItem: (key) => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn('localStorage remove failed:', e);
      }
    }
  };
  
  const [messages, setMessages] = useState(() => {
    try {
      const saved = safeLocalStorage.getItem(getStorageKey('messages'));
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [conversationId, setConversationId] = useState(() => {
    return safeLocalStorage.getItem(getStorageKey('conversationId')) || null;
  });
  // Don't restore isOpen from localStorage on initial load - let auto-open config decide
  const [isOpen, setIsOpen] = useState(false);
  const [showTextSizeMenu, setShowTextSizeMenu] = useState(false);
  const [textSize, setTextSize] = useState(() => {
    return safeLocalStorage.getItem('icebreak-text-size') || 'medium';
  });
  const messagesEndRef = useRef(null);
  const isInitialLoadRef = useRef(true); // Track if this is the initial page load
  
  // Inactivity tracking refs
  const inactivityWarningTimerRef = useRef(null);
  const inactivityCloseTimerRef = useRef(null);
  const hasShownInactivityWarningRef = useRef(false);
  
  // Contact form states
  const [formSettings, setFormSettings] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showFormSuggestion, setShowFormSuggestion] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const changeTextSize = (size) => {
    setTextSize(size);
    safeLocalStorage.setItem('icebreak-text-size', size);
    setShowTextSizeMenu(false);
  };

  // Text size mappings
  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  };

  // Notify parent window of size changes and widget state
  const notifyParentWindow = (type, data = {}) => {
    if (window.parent !== window) {
      window.parent.postMessage({
        type: type,
        ...data
      }, '*');
    }
  };

  // Send ready message when component mounts
  useEffect(() => {
    notifyParentWindow('icebreak-widget-ready');
  }, []);

  // Send resize messages when open state changes
  useEffect(() => {
    if (!isOpen) {
      notifyParentWindow('icebreak-widget-resize', {
        isOpen: false,
        width: '100px',
        height: '100px'
      });
    } else {
      notifyParentWindow('icebreak-widget-resize', {
        isOpen: true,
        width: '400px',
        height: '600px'
      });
    }
  }, [isOpen]);

  useEffect(() => {
    fetchConfig();
    // Hide Emergent banner and add mobile viewport fixes
    const style = document.createElement('style');
    style.innerHTML = `
      body > div[style*="position: fixed"][style*="bottom"] a[href*="emergent"] {
        display: none !important;
      }
      body > div:last-child {
        display: none !important;
      }
      body, html {
        background: transparent !important;
        overflow: hidden;
        margin: 0 !important;
        padding: 0 !important;
        height: 100dvh !important;
        max-height: 100dvh !important;
      }
      #root {
        margin: 0;
        padding: 0;
        height: 100dvh;
        max-height: 100dvh;
      }
      
      /* Mobile-specific fixes for Safari address bar */
      @supports (-webkit-touch-callout: none) {
        body, html, #root {
          height: 100dvh !important;
          max-height: 100dvh !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Force scroll to top on mobile
    if (isMobileDevice()) {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
    
    // Enable parent page interaction
    window.parent.postMessage({type: 'icebreak-widget-interaction'}, '*');
  }, [client_number]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Scroll to bottom when widget opens or user returns to page
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(scrollToBottom, 100);
    }
  }, [isOpen]);
  
  // Scroll to bottom on initial page load if widget is already open
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      setTimeout(scrollToBottom, 200);
    }
  }, []); // Empty dependency array = runs once on mount
  
  // Restore isOpen from localStorage only on subsequent visits (not first-ever visit)
  useEffect(() => {
    // Check if user has visited before
    const hasVisitedBefore = safeLocalStorage.getItem(getStorageKey('hasVisited')) === 'true';
    
    // If user has visited before, restore their previous open state
    // This allows manual open/close to persist across page loads after the first visit
    if (hasVisitedBefore) {
      const savedIsOpen = safeLocalStorage.getItem(getStorageKey('isOpen')) === 'true';
      setIsOpen(savedIsOpen);
      // Since we restored from storage, this is not the initial auto-open scenario
      isInitialLoadRef.current = false;
    }
    // If first-ever visit, leave isOpen as false and let fetchConfig handle auto-open
    // Don't mark as visited yet - let fetchConfig complete first
  }, []); // Run once on mount
  
  useEffect(() => {
    safeLocalStorage.setItem(getStorageKey('messages'), JSON.stringify(messages));
  }, [messages]);
  
  useEffect(() => {
    if (conversationId) {
      safeLocalStorage.setItem(getStorageKey('conversationId'), conversationId);
    } else {
      safeLocalStorage.removeItem(getStorageKey('conversationId'));
    }
  }, [conversationId]);
  
  useEffect(() => {
    safeLocalStorage.setItem(getStorageKey('isOpen'), isOpen.toString());
    
    // Clear inactivity timers when widget is closed
    if (!isOpen) {
      clearInactivityTimers();
    }
  }, [isOpen]);
  
  // Clean up timers on component unmount
  useEffect(() => {
    return () => {
      clearInactivityTimers();
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/chat/config/${client_number}`);
      setConfig(response.data);
      
      // Auto-open widget if configured based on device type (only on initial load)
      // Use user agent detection instead of window.innerWidth (iframe is only 100px wide)
      const isMobile = isMobileDevice();
      const settings = response.data.widget_settings;
      
      // Backward compatibility: if legacy autoOpen exists but new fields don't, use legacy value for both
      const autoOpenDesktop = settings?.autoOpenDesktop ?? settings?.autoOpen ?? false;
      const autoOpenMobile = settings?.autoOpenMobile ?? settings?.autoOpen ?? false;
      
      // Only auto-open on initial page load (first-ever visit)
      if (isInitialLoadRef.current) {
        if (isMobile && autoOpenMobile) {
          setIsOpen(true);
          safeLocalStorage.setItem(getStorageKey('isOpen'), 'true');
        } else if (!isMobile && autoOpenDesktop) {
          setIsOpen(true);
          safeLocalStorage.setItem(getStorageKey('isOpen'), 'true');
        }
        
        // Mark initial load complete and user as having visited
        isInitialLoadRef.current = false;
        safeLocalStorage.setItem(getStorageKey('hasVisited'), 'true');
      }
      
      // Fetch form settings (public endpoint, no auth needed)
      try {
        const formRes = await axios.get(`${API}/public/form-settings/${client_number}`);
        if (formRes.data) {
          setFormSettings(formRes.data);
        }
      } catch (err) {
        console.log('Form settings not available');
      }
    } catch (error) {
      console.error('Failed to load config');
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages([...messages, userMessage]);
    setInputMessage('');
    setLoading(true);
    
    // Reset inactivity timers when user sends a message
    startInactivityTimers();
    
    // Check for trigger phrases in user message
    const shouldShowForm = checkTriggerPhrases(text);

    try {
      const response = await axios.post(`${API}/chat/${client_number}`, {
        client_id: client_number,
        message: text,
        conversation_id: conversationId
      });

      if (!conversationId) {
        setConversationId(response.data.conversation_id);
      }

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        structured: null  // Will hold parsed JSON if available
      };
      
      // Try to parse as JSON (structured response)
      try {
        const structuredData = JSON.parse(response.data.response);
        if (structuredData.answer) {
          aiMessage.structured = structuredData;
          console.log('Parsed structured response:', structuredData);
          console.log('Citations:', structuredData.citations);
        }
      } catch (e) {
        // Not JSON, treat as plain text
        console.log('Response is not JSON, treating as plain text');
      }
      
      setMessages((prev) => [...prev, aiMessage]);
      
      // Check if AI response mentions contact/support - automatically show suggestion box
      const responseText = aiMessage.structured ? aiMessage.structured.answer : response.data.response;
      const aiResponseLower = responseText.toLowerCase();
      const autoOpenKeywords = [
        'contact page',
        'contact form',
        'collect your details',
        'reach our support',
        'support team',
        'teammate follow up',
        'fill out the form',
        'submit your information'
      ];
      
      const shouldAutoShowSuggestion = autoOpenKeywords.some(keyword => 
        aiResponseLower.includes(keyword)
      );
      
      if (shouldAutoShowSuggestion && formSettings && formSettings.enabled) {
        // Auto-show the suggestion box (not the form modal itself)
        setTimeout(() => {
          setShowFormSuggestion(true);
        }, 1000);
      } else if (shouldShowForm) {
        // Show form suggestion if trigger detected in user message
        setShowFormSuggestion(true);
      }
    } catch (error) {
      console.error('Chat error:', error.response?.data || error.message);
      const errorMessage = {
        role: 'assistant',
        content: error.response?.data?.detail || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };
  
  // Clear all inactivity timers
  const clearInactivityTimers = () => {
    if (inactivityWarningTimerRef.current) {
      clearTimeout(inactivityWarningTimerRef.current);
      inactivityWarningTimerRef.current = null;
    }
    if (inactivityCloseTimerRef.current) {
      clearTimeout(inactivityCloseTimerRef.current);
      inactivityCloseTimerRef.current = null;
    }
    hasShownInactivityWarningRef.current = false;
  };
  
  // Send "Are you still there?" message
  const sendInactivityWarning = () => {
    if (hasShownInactivityWarningRef.current || messages.length === 0) {
      return;
    }
    
    hasShownInactivityWarningRef.current = true;
    const warningMessage = {
      role: 'assistant',
      content: 'Are you still there?',
      timestamp: new Date().toISOString()
    };
    setMessages((prev) => [...prev, warningMessage]);
    
    // Start final close timer (1 minute)
    inactivityCloseTimerRef.current = setTimeout(() => {
      handleCloseConversation();
    }, 60000); // 1 minute
  };
  
  // Close conversation due to inactivity
  const handleCloseConversation = () => {
    // Add a closing message
    const closingMessage = {
      role: 'assistant',
      content: 'This conversation has been closed due to inactivity. Click "New Conversation" to start fresh!',
      timestamp: new Date().toISOString()
    };
    setMessages((prev) => [...prev, closingMessage]);
    
    // Clear timers
    clearInactivityTimers();
    
    // Close widget after a short delay
    setTimeout(() => {
      setIsOpen(false);
    }, 2000);
  };
  
  // Start inactivity timers after user sends a message
  const startInactivityTimers = () => {
    clearInactivityTimers();
    
    // Only start timers if there are messages (conversation is active)
    if (messages.length > 0) {
      // Set 3-minute warning timer
      inactivityWarningTimerRef.current = setTimeout(() => {
        sendInactivityWarning();
      }, 180000); // 3 minutes
    }
  };
  
  const checkTriggerPhrases = (text) => {
    if (!formSettings || !formSettings.enabled || !formSettings.trigger_phrases) {
      return false;
    }
    
    const lowerText = text.toLowerCase();
    return formSettings.trigger_phrases.some(phrase => 
      lowerText.includes(phrase.toLowerCase())
    );
  };

  const handleStarterQuestion = (questionItem) => {
    // Handle both old format (string) and new format (object)
    const question = typeof questionItem === 'string' ? questionItem : questionItem.question;
    sendMessage(question);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    setInputMessage('');
    safeLocalStorage.removeItem(getStorageKey('messages'));
    safeLocalStorage.removeItem(getStorageKey('conversationId'));
    clearInactivityTimers();
  };

  const handleSaveTranscript = () => {
    if (messages.length === 0) {
      alert('No messages to save');
      return;
    }

    // Create transcript text
    let transcript = `Chat Transcript - ${config.client_name}\n`;
    transcript += `Date: ${new Date().toLocaleString()}\n`;
    transcript += `${'='.repeat(50)}\n\n`;

    messages.forEach((msg) => {
      const sender = msg.role === 'user' ? 'You' : config.client_name;
      const time = new Date(msg.timestamp).toLocaleTimeString();
      transcript += `[${time}] ${sender}:\n${msg.content}\n\n`;
    });

    // Create and download file
    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-transcript-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };


  const handleSubmitContactForm = async (e) => {
    e.preventDefault();
    
    console.log('=== CONTACT FORM SUBMIT START ===');
    console.log('API URL:', API);
    console.log('Client Number:', client_number);
    console.log('Form Data:', contactFormData);
    
    try {
      console.log('Sending POST request...');
      const response = await axios.post(`${API}/contact-form/${client_number}`, {
        ...contactFormData,
        conversation_id: conversationId
      });
      
      console.log('Response received:', response.data);
      
      // Show success message
      const successMsg = {
        role: 'assistant',
        content: response.data.message || formSettings.success_message,
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, successMsg]);
      
      // Reset and close form
      setContactFormData({ name: '', email: '', phone: '', message: '' });
      setShowContactForm(false);
      setShowFormSuggestion(false);
    } catch (error) {
      console.error('=== CONTACT FORM ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response);
      alert('Failed to send message. Please try again.');
    }
  };


  const formatMessageWithLinks = (text) => {
    // URL regex - matches http/https URLs but stops at parentheses and punctuation
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]()]+)/g;
    
    // Split text by newlines to handle paragraphs
    const paragraphs = text.split('\n\n');
    
    return paragraphs.map((paragraph, pIndex) => {
      // Split each paragraph by newlines to handle bullet points
      const lines = paragraph.split('\n');
      
      return (
        <div key={pIndex} className="mb-3">
          {lines.map((line, lIndex) => {
            // Split line by URLs
            const parts = line.split(urlRegex);
            
            const formattedParts = parts.map((part, index) => {
              if (part && part.match(/^https?:\/\//)) {
                // Clean up URL - remove trailing punctuation
                let cleanUrl = part.replace(/[.,;:!?)]+$/, '');
                
                return (
                  <a
                    key={index}
                    href={cleanUrl}
                    target="_parent"
                    className="text-blue-600 underline hover:text-blue-800 break-words"
                  >
                    {cleanUrl}
                  </a>
                );
              }
              return <span key={index}>{part}</span>;
            });
            
            // Add line break between lines, but proper spacing for bullets
            return (
              <div key={lIndex} className={lIndex < lines.length - 1 ? 'mb-1' : ''}>
                {formattedParts}
              </div>
            );
          })}
        </div>
      );
    });
  };

  if (!config) return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-3 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
    </div>
  );

  const widgetColor = config.widget_settings?.color || '#3B82F6';
  const widgetIcon = config.widget_settings?.icon || 'default';
  const customIconUrl = config.widget_settings?.customIconUrl;

  const iconMap = {
    'default': '💬',
    'robot': '🤖',
    'headset': '🎧',
    'wave': '👋',
    'message': '💭',
    'sparkle': '✨',
  };

  const displayIcon = widgetIcon === 'custom' && customIconUrl ? (
    <img src={customIconUrl} alt="Chat" className="w-8 h-8 rounded-full object-cover" />
  ) : (
    <span className="text-2xl">{iconMap[widgetIcon] || '💬'}</span>
  );
  
  // Get position classes based on config
  const position = config.widget_settings?.position || 'bottom-right';
  const getPositionClasses = () => {
    const positionMap = {
      'bottom-right': {
        button: 'bottom-4 right-4',
        widget: 'md:bottom-4 md:right-4 md:left-auto md:top-auto'
      },
      'bottom-left': {
        button: 'bottom-4 left-4',
        widget: 'md:bottom-4 md:left-4 md:right-auto md:top-auto'
      },
      'top-right': {
        button: 'top-4 right-4',
        widget: 'md:top-4 md:right-4 md:left-auto md:bottom-auto'
      },
      'top-left': {
        button: 'top-4 left-4',
        widget: 'md:top-4 md:left-4 md:right-auto md:bottom-auto'
      }
    };
    return positionMap[position] || positionMap['bottom-right'];
  };
  const positionClasses = getPositionClasses();

  if (!isOpen) {
    return (
      <>
        <style>{`
          @keyframes halo-border {
            0%, 100% {
              box-shadow: 0 0 0 0px ${widgetColor}00;
            }
            50% {
              box-shadow: 0 0 0 4px ${widgetColor}60;
            }
          }
          
          .chat-button-halo {
            animation: halo-border 3s ease-in-out infinite;
          }
        `}</style>
        <div className={`fixed z-50 ${positionClasses.button}`} style={{ border: 'none', outline: 'none' }}>
          <Button
            onClick={() => setIsOpen(true)}
            className="chat-button-halo rounded-full w-16 h-16 hover:scale-110 transition-transform flex items-center justify-center overflow-hidden shadow-lg"
            style={{ backgroundColor: widgetColor, border: 'none', outline: 'none' }}
            data-testid="open-chat-button"
          >
            {displayIcon}
          </Button>
        </div>
      </>
    );
  }

  const isMobile = isMobileDevice();
  
  return (
    <>
      <div className={`fixed ${isMobile ? 'inset-0' : `inset-auto ${positionClasses.widget}`} z-50 ${isMobile ? 'w-auto' : 'w-full max-w-md'}`} data-testid="chat-widget" style={{ height: isMobile ? '100dvh' : 'auto', maxHeight: isMobile ? '100dvh' : 'none', filter: isMobile ? 'none' : 'drop-shadow(0 20px 25px rgba(0, 0, 0, 0.15)) drop-shadow(0 8px 10px rgba(0, 0, 0, 0.1))' }}>
      <Card className={`flex flex-col h-full border-0 overflow-hidden ${isMobile ? 'rounded-none' : 'rounded-lg h-auto'}`} style={{ height: isMobile ? '100%' : '600px', maxHeight: isMobile ? '100%' : '600px' }}>
        {/* Header - Elfsight Style */}
        <div
          className={`p-3 md:p-4 ${isMobile ? '' : 'rounded-t-lg'} flex justify-between items-center text-white flex-shrink-0`}
          style={{ backgroundColor: widgetColor, height: widgetTheme.components.header.height }}
        >
          <div className="flex items-center gap-3">
            {/* Profile Picture */}
            <div className="w-10 h-10 rounded-full overflow-hidden bg-white/20 flex-shrink-0 border-2 border-white/30">
              {widgetIcon === 'custom' && customIconUrl ? (
                <img src={customIconUrl} alt={config.client_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl">
                  {iconMap[widgetIcon] || '💬'}
                </div>
              )}
            </div>
            {/* Name + Support */}
            <div className="flex flex-col">
              <span className="font-semibold text-base leading-tight" data-testid="widget-client-name">
                {config.client_name}
              </span>
              <span className="text-xs text-white/80 leading-tight">Support</span>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {/* Text Size Control */}
            <div className="relative">
              <button
                onClick={() => setShowTextSizeMenu(!showTextSizeMenu)}
                className="hover:bg-white/20 p-1 rounded"
                title="Text Size"
              >
                <Type className="w-4 h-4" />
              </button>
              {showTextSizeMenu && (
                <div className="absolute right-0 top-8 bg-white rounded shadow-lg py-1 z-10 min-w-[100px]">
                  <button
                    onClick={() => changeTextSize('small')}
                    className={`w-full px-3 py-1.5 text-left hover:bg-gray-100 text-gray-800 text-sm ${textSize === 'small' ? 'bg-blue-50 font-semibold' : ''}`}
                  >
                    Small
                  </button>
                  <button
                    onClick={() => changeTextSize('medium')}
                    className={`w-full px-3 py-1.5 text-left hover:bg-gray-100 text-gray-800 text-base ${textSize === 'medium' ? 'bg-blue-50 font-semibold' : ''}`}
                  >
                    Medium
                  </button>
                  <button
                    onClick={() => changeTextSize('large')}
                    className={`w-full px-3 py-1.5 text-left hover:bg-gray-100 text-gray-800 text-lg ${textSize === 'large' ? 'bg-blue-50 font-semibold' : ''}`}
                  >
                    Large
                  </button>
                </div>
              )}
            </div>
            {/* Save Transcript Icon */}
            {messages.length > 0 && (
              <button
                onClick={handleSaveTranscript}
                className="hover:bg-white/20 p-1 rounded"
                title="Save Transcript"
                data-testid="save-transcript-icon"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 p-1 rounded"
              data-testid="close-chat-button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages - Elfsight Style with decorative background */}
        <div 
              className="flex-1 overflow-y-auto p-4 space-y-4 relative" 
              data-testid="messages-container"
              style={{
                backgroundImage: getBackgroundDataURI(widgetColor),
                backgroundColor: '#f8f9fa',
                backgroundSize: '100px 100px',
                backgroundRepeat: 'repeat'
              }}
            >
              {messages.length === 0 && (
                <div className="space-y-4">
                  {/* Welcome Message */}
                  <div className="bg-white p-4 rounded-xl shadow-sm max-w-[85%]">
                    <p className={`${textSizeClasses[textSize]} text-gray-800 leading-relaxed`}>
                      {config.widget_settings?.greetingMessage || `Welcome to ${config.client_name}! How can we assist you today?`}
                    </p>
                    <span className="text-xs text-gray-400 mt-2 block">
                      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  
                  {/* Starter Questions as Pills */}
                  {config.starter_questions.length > 0 && (
                    <div className="flex flex-col items-end gap-2">
                      {config.starter_questions
                        .filter(questionItem => {
                          // Only show questions where show_on_widget is true or not set (default to true)
                          if (typeof questionItem === 'string') return true;
                          return questionItem.show_on_widget !== false;
                        })
                        .map((questionItem, index) => {
                          const question = typeof questionItem === 'string' ? questionItem : questionItem.question;
                          return (
                            <button
                              key={index}
                              onClick={() => handleStarterQuestion(questionItem)}
                              className={`${textSizeClasses[textSize]} px-5 py-2 rounded-full text-white font-medium hover:opacity-90 transition-all hover:scale-105 shadow-md max-w-[90%] text-right`}
                              style={{ 
                                backgroundColor: widgetColor,
                                boxShadow: `0 4px 12px ${widgetColor}40`
                              }}
                              data-testid={`starter-question-${index}`}
                            >
                              {question}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}

              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${index}`}
                >
                  <div
                    className={`max-w-[80%] px-4 pt-4 pb-3 rounded-xl ${
                      msg.role === 'user'
                        ? 'text-white shadow-md'
                        : 'bg-white shadow-sm'
                    }`}
                    style={msg.role === 'user' ? { 
                      backgroundColor: widgetColor,
                      boxShadow: `0 4px 12px ${widgetColor}30`
                    } : {}}
                  >
                    {msg.role === 'assistant' && msg.structured ? (
                      // Render structured JSON response
                      <div className={`${textSizeClasses[textSize]}`}>
                        {msg.structured.tone_intro && (
                          <p className="mb-2 text-gray-600 italic">{msg.structured.tone_intro}</p>
                        )}
                        <div 
                          className="mb-3 prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
                          dangerouslySetInnerHTML={{ __html: msg.structured.answer }}
                          onClick={(e) => {
                            // Close widget on mobile when link is clicked
                            if (e.target.tagName === 'A' && isMobileDevice()) {
                              setIsOpen(false);
                            }
                          }}
                        />
                        
                        {msg.structured.products && msg.structured.products.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {msg.structured.products.map((product, idx) => (
                              <div key={idx} className="border-l-2 border-blue-500 pl-3 py-1">
                                <a 
                                  href={product.url} 
                                  target="_parent"
                                  className="font-semibold text-blue-600 hover:underline"
                                  onClick={() => {
                                    setTimeout(scrollToBottom, 100);
                                    // Close widget on mobile when link is clicked
                                    if (isMobileDevice()) {
                                      setIsOpen(false);
                                    }
                                  }}
                                >
                                  {product.name}
                                </a>
                                {product.price && (
                                  <span className="ml-2 text-green-600 font-medium">{product.price}</span>
                                )}
                                <p className="text-sm text-gray-600 mt-1">{product.why_relevant}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {msg.structured.resources && msg.structured.resources.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {msg.structured.resources.map((resource, idx) => (
                              <div key={idx} className="border-l-2 border-purple-500 pl-3 py-1">
                                <a 
                                  href={resource.url} 
                                  target="_parent"
                                  className="font-semibold text-purple-600 hover:underline"
                                  onClick={() => {
                                    setTimeout(scrollToBottom, 100);
                                    // Close widget on mobile when link is clicked
                                    if (isMobileDevice()) {
                                      setIsOpen(false);
                                    }
                                  }}
                                >
                                  {resource.name}
                                </a>
                                <p className="text-sm text-gray-600 mt-1">{resource.description}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {msg.structured.citations && msg.structured.citations.length > 0 && (
                          <div className="mt-3 space-y-1">
                            {msg.structured.citations.map((citation, idx) => (
                              <div key={idx} className="text-sm">
                                <a 
                                  href={citation} 
                                  target="_parent"
                                  className="text-blue-600 hover:underline break-all"
                                  onClick={() => {
                                    setTimeout(scrollToBottom, 100);
                                    // Close widget on mobile when link is clicked
                                    if (isMobileDevice()) {
                                      setIsOpen(false);
                                    }
                                  }}
                                >
                                  🔗 {citation}
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {msg.structured.follow_up && (
                          <p className="mt-3 text-gray-600 italic text-sm">{msg.structured.follow_up}</p>
                        )}
                      </div>
                    ) : (
                      // Regular text message
                      <p className={`${textSizeClasses[textSize]} whitespace-pre-wrap`}>
                        {formatMessageWithLinks(msg.content)}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start" data-testid="typing-indicator">
                  <div className="bg-white p-3 rounded-xl shadow-sm">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                  </div>
                </div>
              )}

              {/* Form Suggestion - Moved to messages area */}
              {showFormSuggestion && formSettings && formSettings.enabled && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-gray-700 mb-2">
                    Continue your discussion or contact us via the form below
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowFormSuggestion(false)}
                      className="flex-1 text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
                    >
                      Continue Chat
                    </button>
                    <button
                      onClick={() => {
                        setShowContactForm(true);
                        setShowFormSuggestion(false);
                      }}
                      className="flex-1 text-xs px-3 py-1.5 rounded text-white"
                      style={{ backgroundColor: widgetColor }}
                    >
                      Contact Us
                    </button>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input - Elfsight Style */}
            <div className="p-4 bg-white border-t">
              <div className="flex gap-2 items-center">
                <div className="flex-1 relative">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage(inputMessage)}
                    placeholder="Write your message..."
                    disabled={loading}
                    className="rounded-full border-2 border-gray-200 focus:border-gray-300 pr-12 h-12 px-5 text-base"
                    data-testid="message-input"
                  />
                </div>
                <Button
                  onClick={() => sendMessage(inputMessage)}
                  disabled={loading || !inputMessage.trim()}
                  className="rounded-full w-12 h-12 p-0 flex items-center justify-center shadow-md hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: widgetColor }}
                  data-testid="send-message-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2 mt-3 mb-3 justify-center">
                {messages.length > 0 && (
                  <button
                    onClick={handleNewConversation}
                    className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
                    data-testid="new-conversation-button"
                  >
                    <RefreshCw className="w-3 h-3" />
                    New Conversation
                  </button>
                )}
                {formSettings && formSettings.enabled && (
                  <button
                    onClick={() => setShowContactForm(true)}
                    className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
                    data-testid="contact-us-button"
                  >
                    <MessageSquare className="w-3 h-3" />
                    Contact Us
                  </button>
                )}
              </div>
              
              {/* Disclaimer inside the chat widget */}
              {config?.widget_settings?.showDisclaimer && (
                <div className="pt-3 pb-1.5 px-2 bg-white border-t flex items-center justify-center min-h-[40px]">
                  <p className="text-[10px] text-gray-500 text-center leading-relaxed">
                    {config.widget_settings.disclaimerText || "This is an AI-powered assistant. Responses are automated and may not always be accurate or complete. For definitive information, please contact support."}
                  </p>
                </div>
              )}
            </div>
      </Card>
      
      {/* Contact Form Modal */}
      {showContactForm && formSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{formSettings.form_title}</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">{formSettings.form_description}</p>
            
            <form onSubmit={handleSubmitContactForm} className="space-y-3">
              {formSettings.form_fields.name && (
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <Input
                    required
                    value={contactFormData.name}
                    onChange={(e) => setContactFormData({...contactFormData, name: e.target.value})}
                    placeholder="Your name"
                  />
                </div>
              )}
              
              {formSettings.form_fields.email && (
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <Input
                    required
                    type="email"
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData({...contactFormData, email: e.target.value})}
                    placeholder="your@email.com"
                  />
                </div>
              )}
              
              {formSettings.form_fields.phone && (
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input
                    type="tel"
                    value={contactFormData.phone}
                    onChange={(e) => setContactFormData({...contactFormData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              )}
              
              {formSettings.form_fields.message && (
                <div>
                  <label className="block text-sm font-medium mb-1">Message *</label>
                  <textarea
                    required
                    rows="4"
                    value={contactFormData.message}
                    onChange={(e) => setContactFormData({...contactFormData, message: e.target.value})}
                    placeholder="How can we help you?"
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              )}
              
              <div className="flex gap-2 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  style={{ backgroundColor: widgetColor }}
                >
                  Send Message
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
